/**
 * Base API Client
 *
 * Provides configured Axios instance with interceptors for:
 * - Authentication token injection
 * - CSRF token extraction from cookies and header injection
 * - Error handling and transformation
 * - Retry logic for transient failures
 * - Request/response logging in development
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import type { ApiResponse, ApiError } from './types';
import { getCookie } from '../../utils/cookies';
import { logger } from '../../utils/logger';

/**
 * Base client configuration
 */
interface BaseClientConfig {
  baseURL?: string;
  timeout?: number;
  tenantId?: string;
}

/**
 * Extended AxiosRequestConfig with retry tracking
 */
interface AxiosRequestConfigWithRetry extends AxiosRequestConfig {
  __retryCount?: number;
}

/**
 * Generic error response data from server
 */
interface ErrorResponseData {
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
}

/**
 * Storage keys
 */
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'quiz_commerce_access_token',
  REFRESH_TOKEN: 'quiz_commerce_refresh_token',
  TENANT_ID: 'quiz_commerce_tenant_id',
  CSRF_TOKEN: 'quiz_commerce_csrf_token',
} as const;

/**
 * State-changing HTTP methods that require CSRF tokens
 */
const STATE_CHANGING_METHODS = ['post', 'put', 'patch', 'delete'];

/**
 * Default configuration
 */
logger.debug('DEBUG API URL:', import.meta.env.VITE_API_BASE_URL);

const DEFAULT_CONFIG: BaseClientConfig = {
baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1',
  timeout: 10000,
};

/**
 * BaseClient class wrapping Axios with custom interceptors
 */
export class BaseClient {
  private axiosInstance: AxiosInstance;
  private retryCount = 3;
  private retryDelay = 1000; // 1 second

  constructor(config: BaseClientConfig = {}) {
    const mergedConfig = { ...DEFAULT_CONFIG, ...config };

    this.axiosInstance = axios.create({
      baseURL: mergedConfig.baseURL,
      timeout: mergedConfig.timeout,
      withCredentials: true, // Required for CSRF cookies
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    this.setupRequestInterceptors();
    this.setupResponseInterceptors();
  }

  /**
   * Setup request interceptors
   */
  private setupRequestInterceptors(): void {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Inject authentication token
        const token = this.getAccessToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Inject tenant ID
        const tenantId = this.getTenantId();
        if (tenantId && config.headers) {
          config.headers['X-Tenant-ID'] = tenantId;
        }

        // Inject CSRF token for state-changing requests
        // Only POST, PUT, PATCH, DELETE requests need CSRF protection (not GET)
        if (config.method && STATE_CHANGING_METHODS.includes(config.method.toLowerCase())) {
          const csrfToken = this.getCsrfToken();
          if (csrfToken && config.headers) {
            config.headers['X-CSRF-Token'] = csrfToken;

            // Log CSRF token injection in development mode
            if (import.meta.env.DEV) {
              logger.debug(`[CSRF] Token added to ${config.method.toUpperCase()} request:`, config.url);
            }
          } else if (import.meta.env.DEV && !csrfToken) {
            logger.warn(`[CSRF] No CSRF token available for ${config.method.toUpperCase()} request:`, config.url);
          }
        }

        // Log in development
        if (import.meta.env.DEV) {
          logger.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
            params: config.params, // eslint-disable-line @typescript-eslint/no-unsafe-assignment
            data: config.data, // eslint-disable-line @typescript-eslint/no-unsafe-assignment
          });
        }

        return config;
      },
      (error) => {
        if (import.meta.env.DEV) {
          logger.error('[API Request Error]', error);
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Setup response interceptors
   */
  private setupResponseInterceptors(): void {
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // Log in development
        if (import.meta.env.DEV) {
          logger.log(`[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`, {
            status: response.status,
            data: response.data, // eslint-disable-line @typescript-eslint/no-unsafe-assignment
          });
        }

        // Unwrap Axios wrapper — return response.data directly so that
        // consumers receive the backend envelope { success, data } as-is,
        // avoiding the previous double-nesting caused by wrapResponse().
        return response.data;
      },
      async (error: AxiosError) => {
        if (import.meta.env.DEV) {
          logger.error('[API Response Error]', {
            url: error.config?.url,
            status: error.response?.status,
            data: error.response?.data,
          });
        }

        // Handle 401 Unauthorized - attempt token refresh
        if (error.response?.status === 401) {
          const refreshed = await this.handleTokenRefresh();
          if (refreshed && error.config) {
            // Retry the original request
            return this.axiosInstance.request(error.config);
          }
        }

        // Transform error to ApiError
        const apiError = this.transformError(error);

        // Retry logic for transient errors (network issues, 5xx errors)
        if (this.shouldRetry(error) && error.config) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Custom __retryCount property on AxiosRequestConfig
          const configWithRetry = error.config as AxiosRequestConfigWithRetry;
          const retryAttempt = configWithRetry.__retryCount || 0;
          if (retryAttempt < this.retryCount) {
            configWithRetry.__retryCount = retryAttempt + 1;
            await this.delay(this.retryDelay * Math.pow(2, retryAttempt)); // Exponential backoff
            return this.axiosInstance.request(configWithRetry);
          }
        }

        return Promise.reject(apiError);
      }
    );
  }

  /**
   * Transform AxiosError to ApiError
   */
  private transformError(error: AxiosError): ApiError {
    if (error.response) {
      // Server responded with error status - safely extract error data with runtime validation
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Axios response.data is 'any', validated with typeof checks
      const responseData = error.response.data;
      const data: ErrorResponseData =
        responseData && typeof responseData === 'object'
          ? (responseData as ErrorResponseData)
          : {};

      return {
        code: data.code || `HTTP_${error.response.status}`,
        message: data.message || error.message || 'An error occurred',
        details: data.details || { status: error.response.status },
        stack: import.meta.env.DEV ? error.stack : undefined,
      };
    } else if (error.request) {
      // Request made but no response
      return {
        code: 'NETWORK_ERROR',
        message: 'Network error - please check your connection',
        details: { originalError: error.message },
        stack: import.meta.env.DEV ? error.stack : undefined,
      };
    } else {
      // Error setting up request
      return {
        code: 'REQUEST_ERROR',
        message: error.message || 'Failed to make request',
        details: {},
        stack: import.meta.env.DEV ? error.stack : undefined,
      };
    }
  }

  /**
   * Determine if request should be retried
   */
  private shouldRetry(error: AxiosError): boolean {
    if (!error.config) return false;

    // Don't retry POST requests (not idempotent)
    if (error.config.method?.toLowerCase() === 'post') return false;

    // Retry on network errors
    if (!error.response) return true;

    // Retry on 5xx server errors
    const status = error.response.status;
    return status >= 500 && status < 600;
  }

  /**
   * Handle token refresh
   */
  private async handleTokenRefresh(): Promise<boolean> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        this.clearTokens();
        return false;
      }

      // Import dynamically to avoid circular dependency
      const { authService } = await import('./auth-service');
      const response = await authService.refreshToken({ refreshToken });

      if (response.success && response.data) {
        this.setAccessToken(response.data.accessToken);
        return true;
      }

      this.clearTokens();
      return false;
    } catch (error) {
      this.clearTokens();
      return false;
    }
  }

  /**
   * Delay helper for retry logic
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Token management
   */
  private getAccessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  private getTenantId(): string | null {
    return localStorage.getItem(STORAGE_KEYS.TENANT_ID);
  }

  /**
   * Get CSRF token
   *
   * @description
   * Implements dual-source token extraction following the Double Submit Cookie pattern:
   * 1. Primary: Extract token from HTTP cookie (set by backend)
   * 2. Fallback: Retrieve token from sessionStorage (legacy support)
   *
   * The cookie-based approach is preferred as it follows the standard Double Submit
   * Cookie pattern where the server sets an httpOnly=false cookie and the client
   * reads it and sends the value as a request header.
   *
   * @returns CSRF token or null if not available
   */
  private getCsrfToken(): string | null {
    // Primary: Try to extract from cookie (Double Submit Cookie pattern)
    const cookieToken = getCookie('quiz-commerce.x-csrf-token');
    if (cookieToken) {
      // Log in development mode for debugging
      if (import.meta.env.DEV) {
        logger.debug('[CSRF] Token extracted from cookie');
      }
      return cookieToken;
    }

    // Fallback: Try sessionStorage (legacy support or SSR scenarios)
    const storageToken = sessionStorage.getItem(STORAGE_KEYS.CSRF_TOKEN);
    if (storageToken && import.meta.env.DEV) {
      logger.debug('[CSRF] Token extracted from sessionStorage (fallback)');
    }

    return storageToken;
  }

  public setAccessToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  public setRefreshToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  public setTenantId(tenantId: string): void {
    localStorage.setItem(STORAGE_KEYS.TENANT_ID, tenantId);
  }

  public setCsrfToken(token: string): void {
    sessionStorage.setItem(STORAGE_KEYS.CSRF_TOKEN, token);
  }

  private clearTokens(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Fetch CSRF token from server
   *
   * @description
   * Fetches a fresh CSRF token from the backend. The backend will:
   * 1. Generate a new CSRF token
   * 2. Set it as an HTTP cookie (httpOnly=false)
   * 3. Return it in the JSON response
   *
   * This method stores the token in sessionStorage as a fallback, but the
   * primary mechanism is cookie-based extraction via getCsrfToken().
   *
   * Should be called on app initialization to ensure CSRF token is available
   * before any state-changing requests are made.
   *
   * @returns Promise that resolves when token is fetched (or fails silently)
   *
   * @example
   * ```typescript
   * // In App.tsx useEffect
   * useEffect(() => {
   *   baseClient.fetchCsrfToken();
   * }, []);
   * ```
   */
  public async fetchCsrfToken(): Promise<void> {
    try {
      // CSRF endpoint is at /api/csrf-token (not under /api/v1)
      // Construct URL by extracting protocol://host:port and appending /api/csrf-token
      const baseUrl = this.axiosInstance.defaults.baseURL || 'http://localhost:3000/api/v1';
      const urlParts = new URL(baseUrl);
      const csrfUrl = `${urlParts.protocol}//${urlParts.host}/api/csrf-token`;

      if (import.meta.env.DEV) {
        logger.debug('[CSRF] Fetching token from:', csrfUrl);
      }

      const response = await axios.get<{ csrfToken: string }>(
        csrfUrl,
        { withCredentials: true }
      );

      // Store in sessionStorage as fallback
      this.setCsrfToken(response.data.csrfToken);

      if (import.meta.env.DEV) {
        logger.debug('[CSRF] Token fetched successfully and stored in sessionStorage');
        logger.debug('[CSRF] Cookie should be set by backend with httpOnly=false');
      }
    } catch (error) {
      // Silently fail - CSRF token fetch is optional during SSR/build/Lighthouse
      if (import.meta.env.DEV) {
        logger.warn('[CSRF] Token fetch failed (backend may not be available):', error);
      }
    }
  }

  /**
   * HTTP Methods
   */
  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      // After interceptor unwrap, response IS the backend envelope (ApiResponse<T>)
      const response = await this.axiosInstance.get<T>(url, config);
      return response as unknown as ApiResponse<T>;
    } catch (error) {
      return this.wrapError(error as ApiError);
    }
  }

  public async post<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.post<T>(url, data, config);
      return response as unknown as ApiResponse<T>;
    } catch (error) {
      return this.wrapError(error as ApiError);
    }
  }

  public async put<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.put<T>(url, data, config);
      return response as unknown as ApiResponse<T>;
    } catch (error) {
      return this.wrapError(error as ApiError);
    }
  }

  public async patch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.patch<T>(url, data, config);
      return response as unknown as ApiResponse<T>;
    } catch (error) {
      return this.wrapError(error as ApiError);
    }
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.delete<T>(url, config);
      return response as unknown as ApiResponse<T>;
    } catch (error) {
      return this.wrapError(error as ApiError);
    }
  }

  /**
   * Wrap error response
   */
  private wrapError<T>(error: ApiError): ApiResponse<T> {
    return {
      success: false,
      error,
      metadata: {
        timestamp: Date.now(),
        requestId: '',
      },
    };
  }
}

/**
 * Export singleton instance
 */
export const baseClient = new BaseClient();
